import React, { useEffect } from 'react';
import {
  Activity,
  Bot,
  CircleHelp,
  FolderOpen,
  LayoutList,
  ListChecks,
  MessageSquare,
  Sparkles,
  User,
  X,
} from 'lucide-react';
import './LeadsHubHelpModal.css';

export interface ClientWorkspaceHelpModalProps {
  onClose: () => void;
  onTakeTour?: () => void;
}

const ClientWorkspaceHelpModal: React.FC<ClientWorkspaceHelpModalProps> = ({ onClose, onTakeTour }) => {
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="lh-help-overlay" role="presentation" onClick={onClose}>
      <div
        className="lh-help-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cw-help-title"
        onClick={e => e.stopPropagation()}
      >
        <header className="lh-help-header">
          <div className="lh-help-header-main">
            <div className="lh-help-icon" aria-hidden>
              <CircleHelp size={18} />
            </div>
            <div>
              <h2 id="cw-help-title" className="lh-help-title">
                Lead workspace guide
              </h2>
              <p className="lh-help-subtitle">Projects, stages, documents, and AI insights for one lead.</p>
            </div>
          </div>
          <button type="button" className="lh-help-close" onClick={onClose} aria-label="Close help">
            <X size={18} />
          </button>
        </header>

        <div className="lh-help-body">
          <section className="lh-help-section">
            <h3>
              <LayoutList size={16} /> Projects sidebar
            </h3>
            <p>
              Every project for this client is listed on the left with a live <strong>stage chip</strong> — the
              highlighted row is the project you are working on, so you always know which timeline you are viewing.
              Row actions (⋯) rename, transfer, hide, or delete a project. The <strong>portal link and PIN</strong>{' '}
              live at the bottom of the sidebar.
            </p>
          </section>
          <section className="lh-help-section">
            <h3>
              <User size={16} /> Client header
            </h3>
            <p>
              Contact chips, an editable name, a <strong>greyscale map pop-out</strong> (pin button), and a{' '}
              <strong>marketing attribution badge</strong> (click for UTM detail) replaced the old detail cards.
              Edit client info from the pencil button.
            </p>
          </section>
          <section className="lh-help-section">
            <h3>
              <ListChecks size={16} /> Stage rail &amp; advancement
            </h3>
            <p>
              The rail shows the active project's timeline: <strong>solid</strong> steps are completed,{' '}
              <strong>ringed</strong> is the current stage, <strong>muted</strong> steps are locked, and{' '}
              <strong>struck</strong> steps were skipped. Clicking a step only changes what you are viewing.
            </p>
            <p>
              The workflow itself moves through the single <strong>Advance / Retreat footer</strong> under the step
              card. Retreating <strong>pins</strong> the stage where you put it — releases, proposal status, and
              payments will not bump it forward again until you advance manually. Staff and client views compute
              status from the same engine, so they always agree.
            </p>
          </section>
          <section className="lh-help-section">
            <h3>
              <Sparkles size={16} /> Next best actions
            </h3>
            <p>
              The strip under the rail suggests follow-ups computed from the stage and client activity — one click
              runs the action.
            </p>
          </section>
          <section className="lh-help-section">
            <h3>
              <FolderOpen size={16} /> Documents &amp; plans
            </h3>
            <p>
              Upload drawings and filter them by project. The bidding agent writes a <strong>synopsis</strong> on
              each file and drops <strong>markup pins</strong> where it extracts data — click a pin to open the
              viewer at that spot.
            </p>
          </section>
          <section className="lh-help-section">
            <h3>
              <Bot size={16} /> Agents
            </h3>
            <p>
              Open the <strong>Bidding</strong> and <strong>Marketing</strong> agents from the sidebar nav. Bidding
              runs qualification, cost estimates, timber extraction, and proposal prep; Marketing runs enrichment.
              Agent runs show up in the activity feed with an <strong>Agent</strong> tag.
            </p>
          </section>
          <section className="lh-help-section">
            <h3>
              <Activity size={16} /> Activity feed
            </h3>
            <p>
              Client actions stream into the right rail in real time — portal visits, proposal views, signatures,
              payments. Toggle <strong>Staff</strong> to include internal sessions, and use the{' '}
              <strong>Agents</strong> tab to see only AI events.
            </p>
          </section>
          <section className="lh-help-section">
            <h3>
              <MessageSquare size={16} /> Team notes
            </h3>
            <p>
              The collapsible notes strip under documents is for internal coordination — clients never see it.
              Document-specific discussion lives on the document cards and inside the viewer.
            </p>
          </section>
          {onTakeTour && (
            <button type="button" className="lh-help-tour-btn" onClick={onTakeTour}>
              Take the walkthrough
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientWorkspaceHelpModal;
